import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import LoadingSpinner from '../UI/LoadingSpinner';
import Pagination from '../UI/Pagination';
import './PaymentHistory.css';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [currentPage, statusFilter]);

  const fetchPayments = async () => {
    try {
      setLoading(currentPage === 1);
      const response = await paymentService.getPaymentHistory(
        currentPage, 
        itemsPerPage, 
        statusFilter || null
      );
      
      if (response.success) {
        setPayments(response.data.payments);
        setTotalPages(response.data.pagination.pages);
        setTotalPayments(response.data.pagination.total);
      } else {
        throw new Error(response.error || 'Failed to fetch payments');
      }
    } catch (err) {
      console.error('Fetch payments error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await paymentService.getPaymentStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPayments();
    await fetchStats();
    setRefreshing(false);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (orderId) => {
    navigate(`/payments/${orderId}`);
  };

  const handleRetryPayment = async (payment) => {
    try {
      const paymentData = {
        amount: payment.amount,
        customerEmail: payment.customerEmail,
        customerPhone: payment.customerPhone,
        customerName: payment.customerName,
        description: payment.description
      };

      sessionStorage.setItem('retryPaymentData', JSON.stringify(paymentData));
      navigate('/payment', { 
        state: { 
          retryData: paymentData,
          previousOrderId: payment.orderId 
        } 
      });
    } catch (error) {
      console.error('Retry payment error:', error);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.orderId.toLowerCase().includes(searchLower) ||
      payment.customerEmail.toLowerCase().includes(searchLower) ||
      payment.description?.toLowerCase().includes(searchLower) ||
      payment.transactionId?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#ffc107', bg: '#fff3cd', text: '#856404' },
      processing: { color: '#17a2b8', bg: '#d1ecf1', text: '#0c5460' },
      completed: { color: '#28a745', bg: '#d4edda', text: '#155724' },
      failed: { color: '#dc3545', bg: '#f8d7da', text: '#721c24' },
      cancelled: { color: '#6c757d', bg: '#e2e3e5', text: '#383d41' },
      refunded: { color: '#fd7e14', bg: '#ffeaa7', text: '#8a4a00' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: config.bg, 
          color: config.text,
          border: `1px solid ${config.color}20`
        }}
      >
        {paymentService.getStatusIcon(status)} {status?.toUpperCase()}
      </span>
    );
  };

  if (loading && currentPage === 1) {
    return (
      <div className="payment-history-container">
        <div className="loading-state">
          <LoadingSpinner />
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error && payments.length === 0) {
    return (
      <div className="payment-history-container">
        <div className="error-state">
          <div className="error-icon">❌</div>
          <h2>Error Loading Payments</h2>
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history-container">
      <div className="payment-history-header">
        <div className="header-content">
          <h1>Payment History</h1>
          <p>View and manage all your payment transactions</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleRefresh} 
            className="btn btn-outline"
            disabled={refreshing}
          >
            {refreshing ? <LoadingSpinner size="small" /> : '🔄'} Refresh
          </button>
          <Link to="/payment" className="btn btn-primary">
            💳 New Payment
          </Link>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>{paymentService.formatCurrency(stats.totalAmount)}</h3>
              <p>Total Amount</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Payments</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{stats.completed}</h3>
              <p>Successful</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <h3>{stats.failed}</h3>
              <p>Failed</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Order ID, Email, or Description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="search-icon">🔍</div>
        </div>

        <div className="status-filters">
          <button
            onClick={() => handleStatusFilterChange('')}
            className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
          >
            All
          </button>
          <button
            onClick={() => handleStatusFilterChange('completed')}
            className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
          >
            ✅ Completed
          </button>
          <button
            onClick={() => handleStatusFilterChange('pending')}
            className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
          >
            ⏳ Pending
          </button>
          <button
            onClick={() => handleStatusFilterChange('failed')}
            className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
          >
            ❌ Failed
          </button>
        </div>
      </div>

      {/* Payments List */}
      <div className="payments-section">
        {filteredPayments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h3>No Payments Found</h3>
            <p>
              {searchTerm 
                ? 'No payments match your search criteria.' 
                : statusFilter 
                ? `No ${statusFilter} payments found.`
                : 'You haven\'t made any payments yet.'
              }
            </p>
            {!searchTerm && !statusFilter && (
              <Link to="/payment" className="btn btn-primary">
                Make Your First Payment
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="payments-list">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="payment-card">
                  <div className="payment-header">
                    <div className="payment-id">
                      <strong>#{payment.orderId}</strong>
                      {getStatusBadge(payment.status)}
                    </div>
                    <div className="payment-amount">
                      {paymentService.formatCurrency(payment.amount)}
                    </div>
                  </div>

                  <div className="payment-details">
                    <div className="detail-row">
                      <span className="label">Date:</span>
                      <span className="value">
                        {new Date(payment.createdAt).toLocaleDateString()} at{' '}
                        {new Date(payment.createdAt).toLocaleTimeString()}
                      </span>
                    </div>

                    {payment.transactionId && (
                      <div className="detail-row">
                        <span className="label">Transaction ID:</span>
                        <span className="value">{payment.transactionId}</span>
                      </div>
                    )}

                    <div className="detail-row">
                      <span className="label">Email:</span>
                      <span className="value">{payment.customerEmail}</span>
                    </div>

                    {payment.description && (
                      <div className="detail-row">
                        <span className="label">Description:</span>
                        <span className="value">{payment.description}</span>
                      </div>
                    )}

                    {payment.paymentMode && (
                      <div className="detail-row">
                        <span className="label">Payment Method:</span>
                        <span className="value">{payment.paymentMode}</span>
                      </div>
                    )}
                  </div>

                  <div className="payment-actions">
                    <button
                      onClick={() => handleViewDetails(payment.orderId)}
                      className="btn btn-outline btn-small"
                    >
                      👁️ View Details
                    </button>

                    {payment.status === 'failed' && (
                      <button
                        onClick={() => handleRetryPayment(payment)}
                        className="btn btn-primary btn-small"
                      >
                        🔄 Retry Payment
                      </button>
                    )}

                    {payment.status === 'completed' && (
                      <button
                        onClick={() => {/* Implement download receipt */}}
                        className="btn btn-secondary btn-small"
                      >
                        📄 Receipt
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalPayments}
                itemsPerPage={itemsPerPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;